import { inject, injectable } from 'inversify';

import { TYPES } from '@/engine/ioc';
import { TYPES as INDTYPES } from './iocTypes';
import { CORE_TYPES } from '@/engine/app/modules/core';

import { IndicationsRegistry } from './IndicationsRegistry';
import { ModelsPair } from './ModelsPair';

import type { ViewsService } from '../../Views.service';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import type { Tools } from './ToolStateStore';
import type { IIndicationStrategy } from './Indications.controller';
import type { IValue } from '@/engine/DataFlow/Graph';
import type { IBaseViewModel, IMeasurementViewModel, IObjectViewModel, SceneItemKind } from '../../interfaces';
import type { ViewEntity } from '../../models/viewEntity/view.entity.ts';
import type { CasePhaseData } from '../../CasePhaseData/CasePhaseData.ts';
import type { IIndicationVMAdder, IIndicationsViewModelFactory } from './interfaces';
import type { ObservabilityLoggerService } from '@/app.obresvability/loggerService/observabilityLogger.service.ts';

import { createValueAlgorithmBuilder } from '@/engine/PlanningAlgorithm/CustomMeasurementsAlgorithmBuilders.ts';
import type { ICommandProcessor } from '../core/interfaces/Command.ts';
import type { IRemover } from '@/engine/UIPresentation/Interfaces.ts';
import { CancellationController } from '@/engine/Controllers/Cancellation.controller.ts';

@injectable()
export class IndicationsService implements IIndicationVMAdder {
  private _viewsService: ViewsService;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _loggerService: ObservabilityLoggerService;

  private _indicationsRegistry: IndicationsRegistry;

  private _currentIndication?: ModelsPair;

  private readonly _vmFactory: IIndicationsViewModelFactory;

  private readonly _casePhaseData: CasePhaseData;

  private _viewOnWhichIndicationWasStarted?: ViewEntity | null;

  private readonly _commandProcessor: ICommandProcessor;

  private readonly _remover: IRemover;

  private readonly _cancellationController: CancellationController;

  private _toolManagerDisposeHandler: () => void = () => {
    // noop
  };

  public constructor(
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.PlanningAlgorithm)
    planningAlgorithm: PlanningAlgorithm,
    @inject(TYPES.CasePhaseData) casePhaseData: CasePhaseData,
    @inject(INDTYPES.IIndicationsViewModelFactory) vmFactory: IIndicationsViewModelFactory,
    @inject(CORE_TYPES.ObservabilityLoggerService) loggerService: ObservabilityLoggerService,
    @inject(CORE_TYPES.CommandProcessor) commandProcessor: ICommandProcessor,
    @inject(TYPES.IRemover) remover: IRemover,
    @inject(TYPES.CancellationController) cancellationController: CancellationController
  ) {
    this._viewsService = viewsService;
    this._planningAlgorithm = planningAlgorithm;
    this._casePhaseData = casePhaseData;
    this._vmFactory = vmFactory;
    this._loggerService = loggerService;
    this._commandProcessor = commandProcessor;
    this._remover = remover;
    this._cancellationController = cancellationController;
    this._indicationsRegistry = new IndicationsRegistry(this, this._cancellationController);
  }

  public getIndicationStrategyForTool(tool: Tools): IIndicationStrategy {
    const toolFactory = this._indicationsRegistry.getFactoryForTool(tool);
    return toolFactory.createIndicationStrategy(this._planningAlgorithm, this._commandProcessor, this._remover);
  }

  public addMeasurementViewModelToViews(viewModel: IMeasurementViewModel): void {
    this._viewsService.views.forEach((view) => {
      if (this._viewsService.currentView?.getImageStackDescriptor() === view.getImageStackDescriptor()) {
        const measurementToAdd =
          view === this._viewsService.currentView ? viewModel : (viewModel.clone() as IMeasurementViewModel);
        if (view !== this._viewsService.currentView) measurementToAdd.setVisibility(false);
        measurementToAdd.setSelection(false);
        view.addMeasurement(measurementToAdd);
      }
    });
  }

  public addObjectViewModelToViews(viewModel: IObjectViewModel): void {
    this._viewsService.views.forEach((view) => {
      if (this._viewsService.currentView?.getImageStackDescriptor() === view.getImageStackDescriptor()) {
        const objectToAdd = view === this._viewsService.currentView ? viewModel : viewModel.clone();
        if (view !== this._viewsService.currentView) objectToAdd.setVisibility(false);
        objectToAdd.setSelection(false);
        view.addObject(objectToAdd);
      }
    });
  }

  public removeIndicationFromViews(descriptor: string): void {
    this._viewsService.removeFromViewsByDescriptor(descriptor);
  }

  public async initializeIndicationAsync(tool: Tools): Promise<ModelsPair<IValue, IBaseViewModel>> {
    this._viewOnWhichIndicationWasStarted = this._viewsService.currentViewOrThrow;

    if (this._currentIndication) {
      await this.rollbackIndicationAsync();
    }

    const { model, viewModel } = this.createModelViewModel(tool);

    const pair = new ModelsPair(model, viewModel);
    this._currentIndication = pair;

    await this._planningAlgorithm.extendAlgorithmAsync(
      createValueAlgorithmBuilder(viewModel.descriptor, model),
      viewModel.descriptor
    );

    this._viewOnWhichIndicationWasStarted.addObject(viewModel);

    return pair;
  }

  public createModelViewModel(tool: Tools): { model: IValue; viewModel: IObjectViewModel } {
    const toolFactory = this._indicationsRegistry.getFactoryForTool(tool);
    const activePhaseId = this._casePhaseData.getActivePhaseId(
      this._viewsService.currentViewOrThrow.getImageStackDescriptor()
    );
    const viewModel = toolFactory.createIndicationViewModel(this._vmFactory, activePhaseId);
    const model = toolFactory.createIndicationModel();

    return { model, viewModel };
  }

  public async actualizeIndicationAsync(): Promise<void> {
    if (this._currentIndication) {
      await this._planningAlgorithm.setValueAsync(
        this._currentIndication.viewModel.descriptor,
        this._currentIndication.model as IValue
      );
    }
  }

  public async finalizeIndicationAsync(): Promise<void> {
    // TODO: add real finalization logic if applicable
    if (this._currentIndication) {
      await this._planningAlgorithm.setValueAsync(
        this._currentIndication.viewModel.descriptor,
        this._currentIndication.model as IValue
      );
    }
    this._currentIndication = undefined;
    this._toolManagerDisposeHandler();
  }

  public setToolManagerDisposeCallback(callback: () => void): void {
    this._toolManagerDisposeHandler = callback;
  }

  public async rollbackIndicationAsync(): Promise<void> {
    if (this._currentIndication) {
      const viewModel = this._currentIndication.viewModel;
      await this._planningAlgorithm.dropAlgorithmExtensionAsync(viewModel.descriptor);
      this._viewOnWhichIndicationWasStarted?.removeObject(viewModel);
      this._currentIndication = undefined;
    }
  }

  public get vmFactory(): IIndicationsViewModelFactory {
    return this._vmFactory;
  }

  public get loggerService(): ObservabilityLoggerService {
    return this._loggerService;
  }

  public async removeIndicationAsync(viewModel: IObjectViewModel): Promise<void> {
    await this._planningAlgorithm.dropAlgorithmExtensionAsync(viewModel.descriptor);
    this._viewsService.removeFromViewsByDescriptor(viewModel.descriptor);
  }

  public getIndicationLabel(baseLabel: string, kind: SceneItemKind): string {
    if (!this._viewsService.currentView) {
      throw new Error('View should be defined');
    }
    const filtered = [
      ...this._viewsService.currentView.getObjects(),
      ...this._viewsService.currentView.getMeasurements()
    ].filter((obj) => obj.kind === kind);

    // Find the highest number from existing labels
    const highestNumber = filtered.reduce((max, obj) => {
      const match = obj.label.match(new RegExp(`${baseLabel}\\s+(\\d+)`));
      if (match) {
        const num = parseInt(match[1], 10);
        return Math.max(max, num);
      }
      return max;
    }, 0);

    return `${baseLabel} ${highestNumber + 1}`;
  }
}

